// Intent: the ONE analytics entry point for the app. Product analytics (which
// screens people open, where they drop off) via PostHog Cloud EU — but bound by
// two hard rules the whole app depends on:
//   1. OPT-IN. Nothing is collected until the user explicitly turns it on. This
//      backs the privacy promise ("domyślnie nic nie wysyłamy, jesteś anonimowy").
//      Consent is device-level (works for guests too) and stored in kv.
//   2. NO FINANCIAL DATA. Event props go through an allow-list — screen/action
//      names only, never amounts, policy numbers, PESEL or free text.
// On web the transport is a no-op; on native it's PostHog, itself a no-op until
// EXPO_PUBLIC_POSTHOG_KEY is set and the user consents.

import { getDb } from '@/db';

import { transport } from './transport';

const KV_ANALYTICS_CONSENT = 'analytics_consent';

// Props we permit on events. Anything else is dropped before it can leave the
// device — a structural guard so a careless call site can't leak money data.
const ALLOWED_PROP_KEYS = new Set(['screen', 'action', 'source', 'result', 'count']);

let consent = false;
let hydrated = false;

/** Load stored consent and, if granted, spin up the transport. Call once on boot. */
export async function initAnalytics(): Promise<void> {
  if (hydrated) return;
  hydrated = true;
  try {
    const db = await getDb();
    consent = (await db.getKv(KV_ANALYTICS_CONSENT)) === '1';
  } catch {
    consent = false;
  }
  if (consent) {
    await transport.init();
  }
}

export function analyticsConsentGiven(): boolean {
  return consent;
}

/**
 * Turn analytics on/off. Turning on initializes the transport; turning off
 * resets it so no further events (or identifiers) are sent. Persisted to kv.
 */
export async function setAnalyticsConsent(value: boolean): Promise<void> {
  consent = value;
  try {
    const db = await getDb();
    await db.setKv(KV_ANALYTICS_CONSENT, value ? '1' : '0');
  } catch {
    // kv write failing must not crash the toggle; in-memory consent still applies.
  }
  if (value) {
    await transport.init();
  } else {
    transport.reset();
  }
}

/** Record a screen view (no-op unless consented). */
export function trackScreen(name: string): void {
  if (!consent) return;
  transport.screen(name);
}

/** Record an action (no-op unless consented). Props are allow-list filtered. */
export function track(event: string, props?: Record<string, string | number | boolean>): void {
  if (!consent) return;
  transport.capture(event, props ? sanitize(props) : undefined);
}

function sanitize(
  props: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  const clean: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(props)) {
    if (ALLOWED_PROP_KEYS.has(key)) {
      clean[key] = value;
    }
  }
  return clean;
}
